import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent, Typography, Box, LinearProgress, Chip } from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Activity, 
  Users, 
  Star, 
  Calendar, 
  Clock, 
  MapPin, 
  Car, 
  Target, 
  Zap, 
  Award, 
  ShoppingCart,
  Heart,
  UserCheck,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Users2,
  CreditCard
} from 'lucide-react';

const ComprehensiveAnalyticsDashboard = () => {
  const [customerStats, setCustomerStats] = useState(null);
  const [driverStats, setDriverStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAllStats();
  }, [selectedPeriod]);

  const fetchAllStats = async () => {
    try {
      setLoading(true);
      
      // Fetch customer stats from the correct endpoint
      const customerResponse = await fetch(`http://192.168.100.5:3011/commission/analytics/customers-performance?days=${selectedPeriod}`);
      const customerData = await customerResponse.json();
      
      // Fetch drivers from the correct endpoint
      const driversResponse = await fetch(`http://192.168.100.5:3011/driver`);
      const driversData = await driversResponse.json();

      // For now, create mock driver analytics since the comprehensive endpoint doesn't exist
      const driverAnalyticsResults = driversData.map((driver) => ({
        driverId: driver.driver_id,
        driverInfo: driver,
        analytics: {
          earnings: {
            combined: {
              total_earnings: 0,
              available_balance: 0
            }
          },
          trip_statistics: {
            combined: {
              total_trips: 0
            }
          }
        }
      }));

      setCustomerStats(customerData);
      setDriverStats({ drivers: driverAnalyticsResults });
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refreshStats = () => {
    fetchAllStats();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading comprehensive analytics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="text-red-600">
            <Activity className="h-5 w-5" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
            <button 
              onClick={refreshStats}
              className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!customerStats && !driverStats) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500 mb-4">No analytics data available</div>
        <button 
          onClick={refreshStats}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Refresh Data
        </button>
      </div>
    );
  }

  // Calculate metrics with safe defaults
  const totalCustomers = customerStats?.customers?.length || customerStats?.length || 0;
  const totalDrivers = driverStats?.drivers?.length || 0;
  const totalCustomerRevenue = customerStats?.customers?.reduce((sum, c) => sum + (c.total_spent || 0), 0) || 
                           customerStats?.reduce((sum, c) => sum + (c.total_spent || 0), 0) || 0;
  const totalDriverEarnings = driverStats?.drivers?.reduce((sum, d) => sum + (d.analytics?.earnings?.combined?.total_earnings || 0), 0) || 0;
  const totalCustomerTrips = customerStats?.customers?.reduce((sum, c) => sum + (c.total_rides_count || 0), 0) || 
                           customerStats?.reduce((sum, c) => sum + (c.total_rides_count || 0), 0) || 0;
  const totalDriverTrips = driverStats?.drivers?.reduce((sum, d) => sum + (d.analytics?.trip_statistics?.combined?.total_trips || 0), 0) || 0;

  const avgCustomerValue = totalCustomers > 0 ? totalCustomerRevenue / totalCustomers : 0;
  const avgDriverEarnings = totalDrivers > 0 ? totalDriverEarnings / totalDrivers : 0;
  const avgCustomerTrips = totalCustomers > 0 ? totalCustomerTrips / totalCustomers : 0;
  const avgDriverTrips = totalDrivers > 0 ? totalDriverTrips / totalDrivers : 0;

  const totalRevenue = totalCustomerRevenue + totalDriverEarnings;
  const totalTrips = totalCustomerTrips + totalDriverTrips;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Comprehensive Analytics Dashboard</h1>
          <p className="text-gray-600">Complete overview of customer and driver performance metrics</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <button
            onClick={refreshStats}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-md transition-colors ${
            activeTab === 'overview' 
              ? 'bg-white text-blue-600 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('customers')}
          className={`px-4 py-2 rounded-md transition-colors ${
            activeTab === 'customers' 
              ? 'bg-white text-blue-600 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Customers
        </button>
        <button
          onClick={() => setActiveTab('drivers')}
          className={`px-4 py-2 rounded-md transition-colors ${
            activeTab === 'drivers' 
              ? 'bg-white text-blue-600 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Drivers
        </button>
        <button
          onClick={() => setActiveTab('comparison')}
          className={`px-4 py-2 rounded-md transition-colors ${
            activeTab === 'comparison' 
              ? 'bg-white text-blue-600 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Comparison
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Platform Overview */}
          <Card>
            <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Platform Overview
                </Typography>
              }
            />
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600">{totalCustomers + totalDrivers}</div>
                  <div className="text-sm text-blue-800">Total Users</div>
                  <div className="text-xs text-gray-600 mt-1">{totalCustomers} customers, {totalDrivers} drivers</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-3xl font-bold text-green-600">${totalRevenue.toFixed(2)}</div>
                  <div className="text-sm text-green-800">Total Revenue</div>
                  <div className="text-xs text-gray-600 mt-1">Platform earnings</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-3xl font-bold text-purple-600">{totalTrips}</div>
                  <div className="text-sm text-purple-800">Total Trips</div>
                  <div className="text-xs text-gray-600 mt-1">All completed trips</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-3xl font-bold text-orange-600">${(totalRevenue / totalTrips).toFixed(2)}</div>
                  <div className="text-sm text-orange-800">Avg per Trip</div>
                  <div className="text-xs text-gray-600 mt-1">Revenue efficiency</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer vs Driver Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Customer Metrics
                </Typography>
              }
            />
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Total Customers</span>
                  <span className="text-sm font-bold">{totalCustomers}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Total Revenue</span>
                  <span className="text-sm font-bold text-green-600">${totalCustomerRevenue.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Total Trips</span>
                  <span className="text-sm font-bold">{totalCustomerTrips}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Avg Value</span>
                  <span className="text-sm font-bold">${avgCustomerValue.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Avg Trips</span>
                  <span className="text-sm font-bold">{avgCustomerTrips.toFixed(1)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Driver Metrics
                </Typography>
              }
            />
            <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Total Drivers</span>
                    <span className="text-sm font-bold">{totalDrivers}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Total Earnings</span>
                    <span className="text-sm font-bold text-green-600">${totalDriverEarnings.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Total Trips</span>
                    <span className="text-sm font-bold">{totalDriverTrips}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Avg Earnings</span>
                    <span className="text-sm font-bold">${avgDriverEarnings.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Avg Trips</span>
                    <span className="text-sm font-bold">{avgDriverTrips.toFixed(1)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Revenue Distribution */}
          <Card>
            <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Revenue Distribution
                </Typography>
              }
            />
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {((totalCustomerRevenue / totalRevenue) * 100).toFixed(1)}%
                  </div>
                  <div className="text-sm text-blue-800">Customer Revenue</div>
                  <div className="text-xs text-gray-600 mt-1">${totalCustomerRevenue.toFixed(2)}</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {((totalDriverEarnings / totalRevenue) * 100).toFixed(1)}%
                  </div>
                  <div className="text-sm text-green-800">Driver Earnings</div>
                  <div className="text-xs text-gray-600 mt-1">${totalDriverEarnings.toFixed(2)}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Customers Tab */}
      {activeTab === 'customers' && (
        <div className="space-y-6">
          <Card>
            <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Customer Analytics
                </Typography>
              }
            />
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{totalCustomers}</div>
                  <div className="text-sm text-blue-800">Total Customers</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">${totalCustomerRevenue.toFixed(2)}</div>
                  <div className="text-sm text-green-800">Total Spent</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">{totalCustomerTrips}</div>
                  <div className="text-sm text-purple-800">Total Trips</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">${avgCustomerValue.toFixed(2)}</div>
                  <div className="text-sm text-orange-800">Avg Value</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Customers */}
          <Card>
            <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Top Customers by Spending
                </Typography>
              }
            />
            <CardContent>
              <div className="space-y-4">
                {(customerStats?.customers || customerStats || [])
                  .sort((a, b) => (b.total_spent || 0) - (a.total_spent || 0))
                  .slice(0, 10)
                  .map((customer, index) => (
                    <div key={customer.id || customer.customerid} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium">{customer.name}</div>
                          <div className="text-sm text-gray-600">{customer.email}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-green-600">${(customer.total_spent || 0).toFixed(2)}</div>
                        <div className="text-sm text-gray-600">{customer.total_rides_count || 0} rides</div>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Drivers Tab */}
      {activeTab === 'drivers' && (
        <div className="space-y-6">
          <Card>
            <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Driver Analytics
                </Typography>
              }
            />
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{totalDrivers}</div>
                  <div className="text-sm text-blue-800">Total Drivers</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">${totalDriverEarnings.toFixed(2)}</div>
                  <div className="text-sm text-green-800">Total Earnings</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">{totalDriverTrips}</div>
                  <div className="text-sm text-purple-800">Total Trips</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">${avgDriverEarnings.toFixed(2)}</div>
                  <div className="text-sm text-orange-800">Avg Earnings</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Drivers */}
          <Card>
            <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Top Drivers by Earnings
                </Typography>
              }
            />
            <CardContent>
              <div className="space-y-4">
                {(driverStats?.drivers || [])
                  .sort((a, b) => (b.analytics?.earnings?.combined?.total_earnings || 0) - (a.analytics?.earnings?.combined?.total_earnings || 0))
                  .slice(0, 10)
                  .map((driver, index) => (
                    <div key={driver.driverId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium">{driver.driverInfo?.name || driver.driverId}</div>
                          <div className="text-sm text-gray-600">{driver.driverId}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-green-600">
                          ${(driver.analytics?.earnings?.combined?.total_earnings || 0).toFixed(2)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {driver.analytics?.trip_statistics?.combined?.total_trips || 0} trips
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Comparison Tab */}
      {activeTab === 'comparison' && (
        <div className="space-y-6">
          <Card>
            <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Customer vs Driver Performance
                </Typography>
              }
            />
            <CardContent>
              <div className="space-y-6">
                {/* Revenue Comparison */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3">Revenue Comparison</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-800">Customer Revenue</span>
                        <span className="text-sm font-bold text-blue-600">${totalCustomerRevenue.toFixed(2)}</span>
                      </div>
                      <LinearProgress variant="determinate" value={(totalCustomerRevenue / totalRevenue) * 100} className="w-full" />
                      <div className="text-xs text-gray-600 mt-1">
                        {((totalCustomerRevenue / totalRevenue) * 100).toFixed(1)}% of total revenue
                      </div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-green-800">Driver Earnings</span>
                        <span className="text-sm font-bold text-green-600">${totalDriverEarnings.toFixed(2)}</span>
                      </div>
                      <LinearProgress variant="determinate" value={(totalDriverEarnings / totalRevenue) * 100} className="w-full" />
                      <div className="text-xs text-gray-600 mt-1">
                        {((totalDriverEarnings / totalRevenue) * 100).toFixed(1)}% of total revenue
                      </div>
                    </div>
                  </div>
                </div>

                {/* Activity Comparison */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3">Activity Comparison</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-purple-800">Customer Trips</span>
                        <span className="text-sm font-bold text-purple-600">{totalCustomerTrips}</span>
                      </div>
                      <LinearProgress variant="determinate" value={(totalCustomerTrips / totalTrips) * 100} className="w-full" />
                      <div className="text-xs text-gray-600 mt-1">
                        {((totalCustomerTrips / totalTrips) * 100).toFixed(1)}% of total trips
                      </div>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-orange-800">Driver Trips</span>
                        <span className="text-sm font-bold text-orange-600">{totalDriverTrips}</span>
                      </div>
                      <LinearProgress variant="determinate" value={(totalDriverTrips / totalTrips) * 100} className="w-full" />
                      <div className="text-xs text-gray-600 mt-1">
                        {((totalDriverTrips / totalTrips) * 100).toFixed(1)}% of total trips
                      </div>
                    </div>
                  </div>
                </div>

                {/* Value Comparison */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3">Value Comparison</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">${avgCustomerValue.toFixed(2)}</div>
                      <div className="text-sm text-yellow-800">Avg Customer Value</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {avgCustomerValue > avgDriverEarnings ? (
                          <span className="text-green-600">↑ Higher</span>
                        ) : (
                          <span className="text-red-600">↓ Lower</span>
                        )}
                      </div>
                    </div>
                    <div className="text-center p-4 bg-indigo-50 rounded-lg">
                      <div className="text-2xl font-bold text-indigo-600">${avgDriverEarnings.toFixed(2)}</div>
                      <div className="text-sm text-indigo-800">Avg Driver Earnings</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {avgDriverEarnings > avgCustomerValue ? (
                          <span className="text-green-600">↑ Higher</span>
                        ) : (
                          <span className="text-red-600">↓ Lower</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Platform Health Metrics */}
          <Card>
            <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Platform Health Metrics
                </Typography>
              }
            />
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {((totalCustomerRevenue + totalDriverEarnings) / (totalCustomers + totalDrivers)).toFixed(2)}
                  </div>
                  <div className="text-sm text-green-800">Avg Revenue per User</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {(totalTrips / (totalCustomers + totalDrivers)).toFixed(1)}
                  </div>
                  <div className="text-sm text-blue-800">Avg Trips per User</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {totalRevenue > 0 ? ((totalDriverEarnings / totalRevenue) * 100).toFixed(1) : 0}%
                  </div>
                  <div className="text-sm text-purple-800">Driver Revenue Share</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ComprehensiveAnalyticsDashboard;
